import { useState } from "react";
import { MODELS, MUTATIONS, useMultiModel, useMutation } from "../../../../api";
import NotAuthorizedPage from "../../../auth/UnauthorizedView/NotAuthorizedPage.jsx";
import { useDragAutoScroll } from "../../../../hooks/useDragAutoScroll.jsx";
import Button from "../../../../design-system/components/Button/Button.jsx";
import { Variants } from "../../../../design-system/components/Button/Variants.jsx";
import { useSparkContext } from "../../context/SparkContext.jsx";
import StudentListWithoutGroup from "./components/StudentListWithoutGroup/StudentListWithoutGroup.jsx";
import { StudentGroup } from "./components/StudentGroup/StudentGroup.jsx";
import InviteInfoDialog from "./components/InviteInfoDialog/InviteInfoDialog.jsx";
import styles from "./StudentsOverview.css.ts";

const NO_SMALL_TEAM_ID = null;

/**
 * Represents a column layout for displaying student-related content.
 *
 * @param {Object} props - The properties for configuring the StudentColumn component.
 * @param {string} props.heading - The main heading text displayed in the column.
 * @param {string} props.subHeading - The subheading text displayed below the main heading.
 * @param {React.ReactNode} props.headerButton - An optional button or interactive element displayed in the header.
 * @param {React.ReactNode} props.children - The child components or elements to be rendered inside the column.
 * @returns {JSX.Element} A styled column component with a header and a content section.
 */
const StudentColumn = ({ heading, subHeading, headerButton, children }) => {
  return (
    <div className={styles.column}>
      <div className={styles.container}>
        <div className={styles.headerContainer}>
          <h1 className={styles.heading}>{heading}</h1>
          <p className={styles.subHeading}>{subHeading}</p>
        </div>
        {headerButton}
      </div>
      <div className={styles.groupsContainer}>{children}</div>
    </div>
  );
};

/**
 * Renders a collection of student groups based on their small team assignments.
 * We filter students by their small team assignments and render each group with its members.
 *
 * @param {Object} props - The input properties for rendering student groups.
 * @param {Array} props.smallTeams - An array of small team objects, each representing a team.
 * @param {Array} props.students - An array of student objects, each containing details about a student.
 * @param {Function} props.moveStudent - A callback function to handle moving students between teams.
 * @param {Object} props.workshop - The workshop object containing additional workshop-related information.
 *
 * @returns {Array} An array of rendered StudentGroup components corresponding to the provided small teams.
 */
const RenderStudentGroup = ({
  smallTeams,
  students,
  moveStudent,
  workshop,
}) => {
  return smallTeams.map((smallTeam) => {
    const teamMembers = students.filter(
      (student) => student.smallTeamId === smallTeam.id,
    );

    return (
      <StudentGroup
        key={smallTeam.id}
        workshop={workshop}
        smallTeam={smallTeam}
        teamMembers={teamMembers}
        moveStudent={moveStudent}
      />
    );
  });
};

/**
 * Component that displays a list of students along with their assigned groups.
 *
 * Depending on whether small teams exist, this component either renders an overview of student groups
 * or displays a message instructing the user to drag and drop students into groups.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {Array} props.smallTeams - An array of small team objects.
 * @param {Function} props.moveStudent - A function to handle student movement between groups.
 * @param {Array} props.students - An array of student objects.
 * @param {Object} props.workshop - The workshop object representing the current workshop context.
 * @returns {JSX.Element} The rendered component displaying student groups or a message to manage groups.
 */
const StudentListWithGroups = ({
  smallTeams,
  moveStudent,
  students,
  workshop,
}) => {
  const areSmallteamsExisting = smallTeams.length > 0;
  return areSmallteamsExisting ? (
    <RenderStudentGroup
      smallTeams={smallTeams}
      students={students}
      moveStudent={moveStudent}
      workshop={workshop}
    />
  ) : (
    <div className={styles.containerNoGroups}>
      <p className={styles.noGroupsText}>
        Ziehe deine Schüler*innen hierher und manage deine Lerngruppen
      </p>
    </div>
  );
};

/**
 * A React functional component for adding a new group section to a workshop.
 *
 * This component renders a button that triggers the creation of a small team (group section) associated with the provided workshop ID and name.
 * The button reflects a loading state while the request is being processed.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {string} props.workshopId - The unique identifier of the workshop to which the new small team will be added.
 * @param {string} props.name - The name of the small team to be created.
 *
 * @returns {JSX.Element} The rendered button allowing users to create a new small team.
 */
const AddGroupSection = ({ workshopId, name }) => {
  const [waiting, setWaiting] = useState(false);
  const [doAddSmallTeam] = useMutation(MUTATIONS.createSmallTeam);

  async function handleAddSmallTeam() {
    setWaiting(true);

    try {
      await doAddSmallTeam({
        workshopId: workshopId,
        name,
      });
    } catch (error) {
      console.error("Adding a small team failed", error);
    }

    setWaiting(false);
  }

  return (
    <Button
      disabled={waiting}
      width="fit-content"
      onClick={handleAddSmallTeam}
      variant={Variants.PRIMARY}
    >
      {waiting ? "Wird erstellt..." : "Erstellen"}
    </Button>
  );
};

/**
 * StudentsOverviewWithData is a React component that displays an overview of workshop participants,
 * allows invitations to be sent, and manages students within groups.
 *
 * @param {Object} props - The component properties.
 * @param {Object} props.workshop - The current workshop step2.
 * @param {Array} props.students - List of students participating in the workshop.
 * @param {Array} props.smallTeams - List of small teams within the workshop.
 * @param {Function} props.moveStudent - Function to handle moving students between groups.
 */
const StudentsOverviewWithData = ({
  workshop,
  students,
  smallTeams,
  moveStudent,
}) => {
  return (
    <div className={styles.studentsPage}>
      <StudentColumn
        heading="1. Schüler*innen einladen"
        subHeading="Lade zuerst deine Schüler*innen ein"
        placementColumn="left"
        headerButton={<InviteInfoDialog workshopId={workshop.id} />}
      >
        <StudentListWithoutGroup
          students={students}
          moveStudent={moveStudent}
          workshop={workshop}
        />
      </StudentColumn>

      <StudentColumn
        heading="2. Gruppen einteilen"
        subHeading="Erstelle Gruppen für deine Schüler*innen"
        placementColumn="right"
        headerButton={
          <AddGroupSection
            workshopId={workshop.id}
            name={`Gruppe ${smallTeams.length + 1}`}
          />
        }
      >
        <StudentListWithGroups
          smallTeams={smallTeams}
          moveStudent={moveStudent}
          workshop={workshop}
          students={students}
        />
      </StudentColumn>
    </div>
  );
};

/**
 * Displays an overview of students within a specific workshop.
 * Allows teachers and admins to manage small team assignments for students.
 *
 * @function StudentsOverview
 *
 * @return {JSX.Element} A React component that renders the students' overview, or loading spinner, or not authorized page based on the context.
 */
const StudentsOverview = () => {
  const { workshop } = useSparkContext();
  const [userInfo, smallTeams] = useMultiModel([
    MODELS.WORKSHOP_USER_INFO(workshop.id),
    MODELS.WORKSHOP_SMALL_TEAMS(workshop.id),
  ]);

  const [doDeleteUserFromSmallTeam] = useMutation(
    MUTATIONS.deleteUserFromSmallTeam,
  );
  const [doAddUserToSmallTeam] = useMutation(MUTATIONS.addUserToSmallTeam);

  useDragAutoScroll();

  // If I am not a teacher of this course or an admin, return not allowed.
  if (
    !(workshop.roles?.includes("teacher") || workshop.roles?.includes("admin"))
  ) {
    return <NotAuthorizedPage />;
  }

  let students = [];
  if (userInfo.participants)
    students = userInfo.participants.filter((p) => p.role === "student");

  /**
   * Moves a student from one group to another within the workshop.
   *
   * @param {number} studentId - The ID of the student to be moved.
   * @param {number} newGroupId - The ID of the new group to which the student is being moved.
   * @return {void}
   */
  function moveStudent(studentId, newGroupId) {
    const student = students.find((s) => s.id === studentId);
    const oldGroupId = student.smallTeamId;

    if (newGroupId !== NO_SMALL_TEAM_ID) {
      doAddUserToSmallTeam({
        workshopId: workshop.id,
        userId: studentId,
        smallTeamId: newGroupId,
      });
    }

    if (oldGroupId !== NO_SMALL_TEAM_ID && oldGroupId !== newGroupId) {
      doDeleteUserFromSmallTeam({
        workshopId: workshop.id,
        userId: student.id,
        smallTeamId: student.smallTeamId,
      });
    }
  }

  return (
    <StudentsOverviewWithData
      workshop={workshop}
      students={students}
      smallTeams={smallTeams}
      moveStudent={moveStudent}
    />
  );
};

export default StudentsOverview;
